import { FC } from 'react'
import {
  type BlocksContent,
  BlocksRenderer,
} from '@strapi/blocks-react-renderer'
import { TextInlineNode } from '@strapi/blocks-react-renderer/dist/Text'

import Link from '../Link'

import * as SC from './styled'

// not exported by /@strapi/blocks-react-renderer/dist/BlocksRenderer.d.ts
interface LinkInlineNode {
  type: 'link'
  url: string
  children: TextInlineNode[]
}

export type RichTextProps = Pick<MinimalSeoBlock, 'htmlTag'> & {
  className?: string
  html?: string
  jsonContent?: BlocksContent
  lexicon?: {
    pattern: RegExp
    baseUrl: string
  }
}

const RichText: FC<RichTextProps> = ({
  className,
  html,
  htmlTag,
  jsonContent,
  lexicon = { pattern: /((?:lorem|ipsum)(?![0-9]))/i, baseUrl: '/lexique/' },
}) => {
  const parseJsonContent = (jsonContent: BlocksContent): BlocksContent => {
    if (!Array.isArray(jsonContent)) {
      throw new Error('jsonContent must be an array')
    }

    const modifiedContent = jsonContent.map((block) => {
      // paragraph
      if (block.type === 'paragraph') {
        const newBlocks: (TextInlineNode | LinkInlineNode)[] = []
        block.children.forEach((child) => {
          if (child.type !== 'text' || !lexicon) {
            newBlocks.push(child)
          } else {
            const textValue = child.text || ''
            const splitText = textValue.split(lexicon.pattern)

            splitText.forEach((value) => {
              if (!value) return // added because the split can produce 'undefined' entries

              const matchedValue = value.match(lexicon.pattern)?.[0]

              if (matchedValue) {
                newBlocks.push({
                  url: `${lexicon.baseUrl}?search=${matchedValue}`,
                  type: 'link',
                  children: [
                    {
                      ...child,
                      text: matchedValue,
                      type: 'text',
                    },
                  ],
                })
              } else {
                newBlocks.push({
                  ...child,
                  text: value,
                  type: 'text',
                })
              }
            })
          }
        })
        return { type: 'paragraph', children: [...newBlocks] }
      }
      // Other
      else {
        return block
      }
    })

    return modifiedContent as BlocksContent
  }

  const LinkTransformer = (children: React.ReactNode, url: string) => {
    const matchedUrl = url.includes(lexicon.baseUrl)
    const matchedValue = url.match(lexicon.pattern)?.[0]
    return (
      <Link
        href={url}
        {...(matchedUrl && matchedValue
          ? { 'data-lexicon': matchedValue.toLocaleLowerCase() }
          : {})}
      >
        {children}
      </Link>
    )
  }

  return Array.isArray(jsonContent) ? (
    <SC.Container className={className} as={htmlTag}>
      <BlocksRenderer
        content={parseJsonContent(jsonContent)}
        blocks={{
          // For links, you may want to use the component from your router or framework
          link: ({ children, url }) => LinkTransformer(children, url),
        }}
      />
    </SC.Container>
  ) : html ? (
    <SC.Container
      className={className}
      dangerouslySetInnerHTML={{ __html: html ?? '' }}
      as={htmlTag}
    />
  ) : null
}

export default RichText
